import React, { forwardRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { isMobile } from '../../utils/isMobile';

import { MuteIcon, PauseIcon, PlayIcon, SoundIcon } from '../Icons';
import FullScreenIcon from '../Icons/FullScreenIcon';

import './TrailerControls.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

export interface ITrailerControlsProps {
  isPlayingTrailer: boolean;
  hasStartedPlayingTrailer: boolean;
  isTrailerMuted: boolean;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  onToggleFullScreen: () => void;
  hidden: boolean;
  label?: string;
}

const getControlText = (
  hasStartedPlayingTrailer: boolean,
  isPlayingTrailer: boolean
) => {
  if (!hasStartedPlayingTrailer) {
    return 'Watch';
  }

  return isPlayingTrailer ? 'Pause' : 'Play';
};

const TrailerControls = (
  {
    isPlayingTrailer,
    hasStartedPlayingTrailer,
    onTogglePlay,
    isTrailerMuted,
    onToggleMute,
    onToggleFullScreen,
    hidden,
    label = 'Trailer',
  }: ITrailerControlsProps,
  fullScreenButtonRef
) => {
  const yPosition = useReducedMotion() ? '0' : '8px';

  const controlText = getControlText(
    hasStartedPlayingTrailer,
    isPlayingTrailer
  );

  return (
    <ul className="trailer-controls" aria-label="Trailer controls">
      {/* Play Button / Label */}
      <motion.li>
        <div className="trailer-controls__play-button__container">
          <button
            className="trailer-controls__play-button trailer-controls__button play"
            title={`${controlText} ${label}`}
            aria-label={`${controlText} ${label}`}
            aria-pressed={isPlayingTrailer}
            aria-hidden={hidden}
            tabIndex={hidden ? -1 : 0}
            onClick={() => {
              if (isPlayingTrailer) {
                tealiumTrackEvent({
                  linktype: 'hero__trailer-pause-button',
                  linkname: `Pause ${label}`,
                });
              } else {
                tealiumTrackEvent({
                  linktype: 'hero__trailer-play-button',
                  linkname: `Play ${label}`,
                });
              }
              onTogglePlay();
            }}
            data-linkname={
              isPlayingTrailer ? `Pause ${label}` : `Play ${label}`
            }
            data-linktype={
              isPlayingTrailer
                ? 'hero__trailer-pause-button'
                : 'hero__trailer-play-button'
            }
          >
            {isPlayingTrailer ? (
              <PauseIcon color="#31a2ff" />
            ) : (
              <PlayIcon color="#31a2ff" />
            )}
          </button>
          <AnimatePresence>
            {!hasStartedPlayingTrailer && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: {
                    delay: 0.3,
                    type: 'tween',
                    ease: 'linear',
                    duration: 0.2,
                  },
                }}
                exit={{
                  opacity: 0,
                  transition: {
                    type: 'tween',
                    ease: 'linear',
                    duration: 0.2,
                  },
                }}
                aria-hidden={true}
                className="trailer-controls__play-button__text"
              >{`${controlText} ${label}`}</motion.span>
            )}
          </AnimatePresence>
        </div>
      </motion.li>
      <AnimatePresence>
        {hasStartedPlayingTrailer && !isMobile.iOSDevice() ? (
          <>
            {/* Mute Button */}
            <motion.li
              initial={{ opacity: 0, y: yPosition }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.1,
                  duration: 0.8,
                },
              }}
              exit={{
                opacity: 0,
                transition: { ease: 'linear', duration: 0.2 },
              }}
              transition={{ type: 'tween' }}
            >
              <button
                className="trailer-controls__button trailer-controls__button--secondary mute"
                title={`${isTrailerMuted ? 'Unmute' : 'Mute'} Trailer`}
                aria-label={`${isTrailerMuted ? 'Unmute' : 'Mute'} Trailer`}
                aria-pressed={isTrailerMuted}
                aria-hidden={hidden}
                tabIndex={hidden ? -1 : 0}
                onClick={onToggleMute}
              >
                {isTrailerMuted ? <MuteIcon /> : <SoundIcon />}
              </button>
            </motion.li>
            {/* Full Screen Button */}
            <motion.li
              initial={{ opacity: 0, y: yPosition }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.2,
                  duration: 0.8,
                },
              }}
              exit={{
                opacity: 0,
                transition: { ease: 'linear', duration: 0.2 },
              }}
              transition={{ type: 'tween' }}
            >
              <button
                className="trailer-controls__button trailer-controls__button--secondary"
                title="Full Screen"
                aria-label="Full Screen"
                aria-hidden={hidden}
                tabIndex={hidden ? -1 : 0}
                onClick={onToggleFullScreen}
                ref={fullScreenButtonRef}
              >
                <FullScreenIcon />
              </button>
            </motion.li>
          </>
        ) : hasStartedPlayingTrailer &&
          isMobile.iOSTablet() &&
          isMobile.Chrome() ? (
          // Mute Button
          <motion.li
            initial={{ opacity: 0, y: yPosition }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                ease: [0.22, 1, 0.36, 1],
                delay: 0.1,
                duration: 0.8,
              },
            }}
            exit={{
              opacity: 0,
              transition: { ease: 'linear', duration: 0.2 },
            }}
            transition={{ type: 'tween' }}
          >
            <button
              className="trailer-controls__button trailer-controls__button--secondary mute"
              title={`${isTrailerMuted ? 'Unmute' : 'Mute'} Trailer`}
              aria-label={`${isTrailerMuted ? 'Unmute' : 'Mute'} Trailer`}
              aria-pressed={isTrailerMuted}
              aria-hidden={hidden}
              tabIndex={hidden ? -1 : 0}
              onClick={onToggleMute}
            >
              {isTrailerMuted ? <MuteIcon /> : <SoundIcon />}
            </button>
          </motion.li>
        ) : null}
      </AnimatePresence>
    </ul>
  );
};

export default forwardRef(TrailerControls);
